import {
  Component, Input, AfterViewInit, OnDestroy, ElementRef,
  ViewChild, PLATFORM_ID, inject
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export interface MapMarker {
  lat: number;
  lng: number;
  label: string;
  description?: string;
}

@Component({
  selector: 'app-map',
  standalone: true,
  template: `<div #mapContainer class="w-full h-full rounded-2xl overflow-hidden"></div>`,
})
export class MapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer') mapContainer!: ElementRef<HTMLDivElement>;
  @Input() lat = 25.2048;
  @Input() lng = 55.2708;
  @Input() zoom = 5;
  @Input() markers: MapMarker[] = [];
  @Input() darkMode = false;

  private map: any = null;
  private platformId = inject(PLATFORM_ID);

  async ngAfterViewInit(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const L = await import('leaflet');

    this.map = L.map(this.mapContainer.nativeElement, {
      scrollWheelZoom: false,
      attributionControl: false,
    }).setView([this.lat, this.lng], this.zoom);

    const tileUrl = this.darkMode
      ? 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
      : 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';

    L.tileLayer(tileUrl, {
      maxZoom: 19,
      subdomains: 'abcd',
    }).addTo(this.map);

    const icon = L.divIcon({
      className: 'custom-marker',
      html: `<div style="
        width: 14px; height: 14px;
        background: #c8951d;
        border: 2.5px solid #fff;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.25);
      "></div>`,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });

    for (const m of this.markers) {
      const marker = L.marker([m.lat, m.lng], { icon }).addTo(this.map);
      if (m.label) {
        marker.bindPopup(`
          <div style="font-family: 'Outfit', sans-serif;">
            <strong style="font-size: 14px; color: #1e1d1a;">${m.label}</strong>
            ${m.description ? `<p style="margin: 4px 0 0; color: #7d7a72; font-size: 13px;">${m.description}</p>` : ''}
          </div>
        `);
      }
    }

    // Fit bounds if multiple markers
    if (this.markers.length > 1) {
      const group = L.featureGroup(
        this.markers.map(m => L.marker([m.lat, m.lng]))
      );
      this.map.fitBounds(group.getBounds().pad(0.3));
    }
  }

  ngOnDestroy(): void {
    this.map?.remove();
  }
}
